import * as React from "react";
import clsx from "clsx";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Text } from "../../../ui/components/Text";
import type { ProductAccessTier } from "../../lib/api/product-access";
import { PRODUCT_ACCESS_TIER_OPTIONS } from "../../lib/api/product-access";
import { useI18n } from "../../lib/i18n";
import { ProductAccessTierBadge } from "./ProductAccessTierBadge";
import * as s from "./ProductPage.css";

export const PRODUCT_ACCESS_TIER_DESCRIPTIONS: Record<ProductAccessTier, string> =
  {
    view: "See product status, telemetry, and history. Cannot change settings.",
    edit: "Update product settings and configuration. Cannot manage who has access.",
    full: "Full control, including inviting others and managing product access.",
  };

export function RequestProductAccessForm({
  productName,
  mode,
  currentTier,
  currentAccessTitle,
  initialTier = "view",
  pending,
  pendingTier,
  submitting,
  cancelling,
  error,
  onSubmit,
  onCancel,
  onCancelRequest,
}: {
  productName?: string | null;
  mode: "request" | "change";
  currentTier?: ProductAccessTier | null;
  /** Tooltip for the current-access chip (e.g. inherited source). */
  currentAccessTitle?: string;
  initialTier?: ProductAccessTier;
  pending?: boolean;
  pendingTier?: ProductAccessTier | null;
  submitting?: boolean;
  cancelling?: boolean;
  error?: string | null;
  onSubmit: (tier: ProductAccessTier) => void | Promise<void>;
  onCancel?: () => void;
  onCancelRequest?: () => void | Promise<void>;
}) {
  const { t } = useI18n();
  const [tier, setTier] = React.useState<ProductAccessTier>(initialTier);

  React.useEffect(() => {
    setTier(initialTier);
  }, [initialTier]);

  const title =
    mode === "change" ? "Request different access" : "Request product access";
  const subtitle =
    mode === "change"
      ? `Choose the access level you need for${productName ? ` ${productName}` : " this product"}.`
      : `You do not have permission to view${productName ? ` ${productName}` : " this product"}. Choose an access level to request.`;

  const busy = Boolean(submitting || cancelling);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Text level="h3" weight="bold" style={{ margin: "0 0 6px" }}>
          {title}
        </Text>
        <Text level="body-medium" style={{ margin: 0 }}>
          {subtitle}
        </Text>
      </div>

      {mode === "change" && currentTier ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Text level="body-small" style={{ margin: 0, opacity: 0.7 }}>
            Your current access
          </Text>
          <ProductAccessTierBadge
            tier={currentTier}
            title={currentAccessTitle}
            showShield
          />
        </div>
      ) : null}

      {pending ? (
        <Callout variant="info">
          <span
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 10,
              width: "100%",
            }}
          >
            <span
              style={{
                flex: "1 1 200px",
                display: "inline-flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
              }}
            >
              Your request for
              <ProductAccessTierBadge tier={pendingTier ?? "view"} />
              is pending. An administrator will review it shortly.
            </span>
            {onCancelRequest ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => void onCancelRequest()}
                style={{ marginLeft: "auto" }}
              >
                {cancelling ? "Canceling…" : "Cancel request"}
              </Button>
            ) : null}
          </span>
        </Callout>
      ) : null}

      <div
        role="radiogroup"
        aria-label={t("pro.products.accessLevel")}
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        {PRODUCT_ACCESS_TIER_OPTIONS.map((opt) => {
          const selected = tier === opt.value;
          const isCurrent = currentTier === opt.value;
          return (
            <label
              key={opt.value}
              data-pending={pending ? "true" : undefined}
              className={clsx(
                s.accessTierOption,
                selected && s.accessTierOptionSelected,
              )}
            >
              <input
                type="radio"
                name="product-access-tier"
                value={opt.value}
                checked={selected}
                disabled={Boolean(pending)}
                onChange={() => setTier(opt.value)}
                style={{ marginTop: 3 }}
              />
              <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <ProductAccessTierBadge tier={opt.value} />
                  {isCurrent ? (
                    <Text level="body-small" style={{ opacity: 0.65, margin: 0 }}>
                      Current
                    </Text>
                  ) : null}
                </span>
                <Text level="body-small" style={{ margin: 0, opacity: 0.75 }}>
                  {PRODUCT_ACCESS_TIER_DESCRIPTIONS[opt.value]}
                </Text>
              </span>
            </label>
          );
        })}
      </div>

      {error ? <Callout variant="error">{error}</Callout> : null}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 4,
        }}
      >
        {onCancel ? (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
        ) : null}
        <Button
          type="button"
          color="primary"
          disabled={Boolean(pending) || busy || (mode === "change" && tier === currentTier)}
          onClick={() => void onSubmit(tier)}
        >
          {submitting
            ? "Submitting…"
            : mode === "change"
              ? "Request change"
              : "Request access"}
        </Button>
      </div>
    </div>
  );
}
